// Opens a native <dialog> containing a Cropper.js editor and either:
//   - submits the cropped result as a File via a hidden form (mode: "blob")
//   - or submits the crop box coordinates as JSON via fetch (mode: "metadata")
//
// Metadata mode is used for non-destructive crop-in-place on ProfileMedium:
// the server stores only the crop rect, the original blob is never touched,
// and re-opening the dialog pre-fills Cropper with the saved box.
//
// Example blob-mode usage (crop result is a new File uploaded via form):
//   <div data-controller="image-crop"
//        data-image-crop-src-value="/path/to/image.jpg"
//        data-image-crop-filename-value="photo.jpg"
//        data-image-crop-aspect-ratio-value="1"
//        data-image-crop-mode-value="blob">
//     ...
//   </div>
//
// Example metadata-mode usage (crop result is x/y/w/h posted as JSON):
//   <div data-controller="image-crop"
//        data-image-crop-src-value="/path/to/image.jpg"
//        data-image-crop-mode-value="metadata"
//        data-image-crop-crop-box-value='{"x":10,"y":20,"width":300,"height":400}'>
//     ...
//     <form data-image-crop-target="form" action="/crop" method="post">...</form>
//     ...
//   </div>

import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"
import Cropper from "cropperjs"

export default class extends Controller {
  static targets = ["dialog", "image", "form", "fileField"]

  static values = {
    src: String,
    filename: { type: String, default: "cropped.webp" },
    aspectRatio: { type: Number, default: NaN },
    mimeType: { type: String, default: "image/webp" },
    mode: { type: String, default: "blob" },
    cropBox: { type: Object, default: {} },
    formMethod: { type: String, default: "POST" }
  }

  disconnect() {
    this.destroyCropper()
    this._unpinAncestor()
  }

  open(event) {
    event?.preventDefault()

    // The dialog may live inside a `hidden group-hover:flex` container.
    // When the dialog is in the top layer (showModal) the browser removes it
    // if its ancestor goes display:none (hover lost). Pin the container
    // visible while the dialog is open so this can't happen.
    this._pinAncestorVisible()

    this.imageTarget.src = this.srcValue
    this.dialogTarget.showModal()

    this.destroyCropper()
    this.cropper = new Cropper(this.imageTarget, {
      viewMode: 1,
      dragMode: "move",
      aspectRatio: Number.isFinite(this.aspectRatioValue) ? this.aspectRatioValue : NaN,
      autoCropArea: 1,
      movable: true,
      zoomable: true,
      scalable: false,
      rotatable: false,
      background: false,
      responsive: true,
      ready: () => this.applySavedCropBox()
    })
  }

  applySavedCropBox() {
    const box = this.cropBoxValue
    if (!box || box.width == null || box.height == null) return
    if (!this.cropper) return

    this.cropper.setData({
      x: Number(box.x) || 0,
      y: Number(box.y) || 0,
      width: Number(box.width),
      height: Number(box.height)
    })
  }

  close(event) {
    event?.preventDefault()
    this.destroyCropper()
    if (this.dialogTarget.open) this.dialogTarget.close()
    this._unpinAncestor()
  }

  save(event) {
    event?.preventDefault()
    if (!this.cropper) return

    if (this.modeValue === "metadata") {
      this.saveMetadata()
    } else {
      this.saveBlob()
    }
  }

  saveBlob() {
    const canvas = this.cropper.getCroppedCanvas({
      maxWidth: 4096,
      maxHeight: 4096,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })
    if (!canvas) return

    canvas.toBlob((blob) => {
      if (!blob) return

      const file = new File([blob], this.filenameValue, { type: this.mimeTypeValue })
      const transfer = new DataTransfer()
      transfer.items.add(file)
      this.fileFieldTarget.files = transfer.files

      this.destroyCropper()
      if (this.dialogTarget.open) this.dialogTarget.close()
      this._unpinAncestor()

      this.formTarget.requestSubmit()
    }, this.mimeTypeValue, 0.92)
  }

  async saveMetadata() {
    const data = this.cropper.getData({ rounded: true })
    const body = {
      x: Math.max(0, data.x),
      y: Math.max(0, data.y),
      width: Math.max(1, data.width),
      height: Math.max(1, data.height)
    }

    // FetchRequest handles CSRF and auto-renders the turbo-stream response.
    // The crop endpoint replies by reloading the profile_media_grid frame —
    // which contains this very dialog — so we close/clean up *before*
    // performing, mirroring the original close-then-render order and avoiding
    // operating on a node the reload tears out from under us.
    const request = new FetchRequest(this.formMethodValue, this.formTarget.action, {
      body,
      responseKind: "turbo-stream"
    })

    this.destroyCropper()
    if (this.dialogTarget.open) this.dialogTarget.close()
    this._unpinAncestor()

    await request.perform()
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  // Finds the nearest `hidden` ancestor and swaps it to `flex` so the dialog
  // stays rendered while open. Without this, group-hover containers revert to
  // display:none when the pointer leaves, and the browser yanks the dialog
  // out of the top layer.
  _pinAncestorVisible() {
    this._pinnedAncestor = this.element.closest(".hidden")
    if (!this._pinnedAncestor) return
    this._pinnedAncestor.classList.remove("hidden")
    this._pinnedAncestor.classList.add("flex")
  }

  _unpinAncestor() {
    if (!this._pinnedAncestor) return
    this._pinnedAncestor.classList.remove("flex")
    this._pinnedAncestor.classList.add("hidden")
    this._pinnedAncestor = null
  }
}
